import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { fetchMe, login as apiLogin, logoutApi, setTokens, clearTokens, getAccessToken, googleAuthApi } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // user states: undefined = checking, null = not authenticated, object = authenticated
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    if (!getAccessToken()) {
      setUser(null);
      return;
    }
    try {
      const me = await fetchMe();
      setUser(me);
    } catch {
      clearTokens();
      setUser(null);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const data = await apiLogin(email, password);
    setTokens(data.access_token, data.refresh_token);
    setUser(data.user);
    return data.user;
  }, []);

  const loginWithGoogleSession = useCallback(async (session_id) => {
    const data = await googleAuthApi(session_id);
    setTokens(data.access_token, data.refresh_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    await logoutApi();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, login, logout, refresh, loginWithGoogleSession }),
    [user, login, logout, refresh, loginWithGoogleSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
